/**
 * Better Life Clean — theme.js
 * Shared JS: cart AJAX, scroll reveal, magnetic buttons,
 * product card cursor light, fly-to-cart, mobile nav.
 * Vanilla JS only. No animation libraries.
 */
(function () {
  'use strict';

  /* =========================================================
     CART — Shopify AJAX Cart API wrapper
     Replaces the static mock from shared/cart.js
     ========================================================= */
  window.BLCCart = {
    _count: 0,

    async get() {
      const r = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
      return r.json();
    },

    async add(variantId, qty, sellingPlanId) {
      const item = { id: variantId, quantity: qty || 1 };
      if (sellingPlanId) item.selling_plan = sellingPlanId;
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      });
      const json = await r.json();
      if (!json.items && json.status) {
        console.error('Cart add error:', json.description);
        return null;
      }
      await this.refresh();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return json;
    },

    async change(key, qty) {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      const json = await r.json();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return json;
    },

    async remove(key) {
      return this.change(key, 0);
    },

    async refresh() {
      const cart = await this.get();
      this._count = cart.item_count || 0;
      this._updateBadges();
      await this.renderDrawer(cart);
      return cart;
    },

    _updateBadges() {
      document.querySelectorAll('.blc-cart-count').forEach(el => {
        el.textContent = this._count;
        el.classList.toggle('is-hidden', this._count === 0);
      });
      // Pulse animation
      document.querySelectorAll('.blc-cart-btn').forEach(btn => {
        btn.classList.remove('blc-pulse');
        void btn.offsetWidth;
        btn.classList.add('blc-pulse');
      });
    },

    open() {
      const drawer = document.getElementById('blc-cart-drawer');
      if (drawer) {
        drawer.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        this.refresh();
      }
    },

    close() {
      const drawer = document.getElementById('blc-cart-drawer');
      if (drawer) {
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    },

    count() { return this._count; },

    async renderDrawer(cart) {
      const body = document.querySelector('.blc-cart-body');
      if (!body) return;

      if (!cart) cart = await this.get();

      if (!cart.items || cart.items.length === 0) {
        body.innerHTML = `
          <div class="blc-cart-empty">
            <div class="blc-empty-bottle">🧴</div>
            <h3>Your cart is empty</h3>
            <p>Add a cleaner and let's get this party started.</p>
            <a href="/collections/all" class="blc-btn blc-btn-primary">Shop the Range <span class="arrow">→</span></a>
          </div>`;
        document.querySelector('.blc-cart-total-val') && (document.querySelector('.blc-cart-total-val').textContent = BLCFormat.money(0));
        return;
      }

      body.innerHTML = cart.items.map(item => `
        <div class="blc-cart-line" data-key="${item.key}">
          <div class="blc-line-img" style="--ps: ${item.properties && item.properties._brand_color ? item.properties._brand_color : 'var(--off)'}">
            <img src="${item.image ? item.image.replace('.jpg', '_120x.jpg').replace('.png', '_120x.png') : ''}" alt="${item.title}" loading="lazy">
          </div>
          <div class="blc-line-info">
            <div class="blc-line-name">${item.product_title}</div>
            <div class="blc-line-var">${item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : ''}</div>
            ${item.selling_plan_allocation ? `<span class="blc-line-sub">Subscribe &amp; Save</span>` : ''}
            <div class="blc-line-controls">
              <div class="blc-qty">
                <button class="blc-qty-dec blc-qty__btn" aria-label="Decrease quantity" data-key="${item.key}" data-qty="${item.quantity - 1}">−</button>
                <span class="blc-qty__val">${item.quantity}</span>
                <button class="blc-qty-inc blc-qty__btn" aria-label="Increase quantity" data-key="${item.key}" data-qty="${item.quantity + 1}">+</button>
              </div>
              <button class="blc-line-remove" data-key="${item.key}">Remove</button>
            </div>
          </div>
          <div class="blc-line-price">${BLCFormat.money(item.final_line_price)}</div>
        </div>`).join('');

      // Wire qty buttons
      body.querySelectorAll('.blc-qty-dec, .blc-qty-inc').forEach(btn => {
        btn.addEventListener('click', () => {
          const qty = parseInt(btn.dataset.qty, 10);
          this.change(btn.dataset.key, Math.max(0, qty));
        });
      });
      body.querySelectorAll('.blc-line-remove').forEach(btn => {
        btn.addEventListener('click', () => this.remove(btn.dataset.key));
      });

      // Update totals
      const totalEl = document.querySelector('.blc-cart-total-val');
      if (totalEl) totalEl.textContent = BLCFormat.money(cart.total_price);

      // Free shipping nudge
      const nudge = document.querySelector('.blc-cart-nudge');
      if (nudge) {
        const threshold = 4500; // $45.00
        const remaining = Math.max(0, threshold - cart.total_price);
        if (remaining > 0) {
          nudge.innerHTML = `
            <span>Add ${BLCFormat.money(remaining)} for free shipping!</span>
            <div class="bar"><div class="bar-fill" style="width:${Math.min(100,(cart.total_price/threshold*100))}%"></div></div>`;
          nudge.style.display = '';
        } else {
          nudge.innerHTML = `<span>🎉 You've unlocked free shipping!</span>`;
        }
      }
    }
  };

  /* =========================================================
     MONEY FORMATTER
     ========================================================= */
  window.BLCFormat = {
    money(cents) {
      return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  };

  /* =========================================================
     FLY-TO-CART ANIMATION
     ========================================================= */
  window.BLC_FLY_TO_CART = function (fromEl, imgSrc) {
    const cart = document.querySelector('.blc-cart-btn');
    if (!cart || !fromEl) return;
    const r1 = fromEl.getBoundingClientRect();
    const r2 = cart.getBoundingClientRect();
    const fly = document.createElement('div');
    fly.className = 'blc-fly-bottle';
    fly.innerHTML = `<img src="${imgSrc}" alt="">`;
    fly.style.cssText = `left:${r1.left + r1.width / 2 - 30}px;top:${r1.top + window.scrollY + r1.height / 2 - 40}px;position:fixed;`;
    document.body.appendChild(fly);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fly.style.left = r2.left + r2.width / 2 - 30 + 'px';
        fly.style.top  = r2.top  + r2.height / 2 - 40 + 'px';
        fly.style.transform = 'scale(0.15) rotate(360deg)';
        fly.style.opacity = '0';
      });
    });
    setTimeout(() => fly.remove(), 900);
  };

  /* =========================================================
     DOM READY — wire everything up
     ========================================================= */
  document.addEventListener('DOMContentLoaded', () => {

    /* -- Noise overlay -- */
    if (!document.querySelector('.blc-noise')) {
      const noise = document.createElement('div');
      noise.className = 'blc-noise';
      document.body.appendChild(noise);
    }

    /* -- Cart button triggers -- */
    document.querySelectorAll('.blc-cart-btn').forEach(btn => {
      btn.addEventListener('click', () => BLCCart.open());
    });

    /* -- Cart drawer close -- */
    document.querySelector('.blc-cart-scrim')?.addEventListener('click', () => BLCCart.close());
    document.querySelector('.blc-cart-close')?.addEventListener('click', () => BLCCart.close());

    /* -- Escape key closes cart -- */
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') BLCCart.close();
    });

    /* -- Initial cart count -- */
    BLCCart.get().then(cart => {
      BLCCart._count = cart.item_count || 0;
      BLCCart._updateBadges();
    });

    /* -- cart:updated event listener -- */
    document.addEventListener('cart:updated', () => {
      BLCCart.get().then(cart => {
        BLCCart._count = cart.item_count || 0;
        BLCCart._updateBadges();
        if (document.getElementById('blc-cart-drawer')?.classList.contains('is-open')) {
          BLCCart.renderDrawer(cart);
        }
      });
    });

    /* -- Scroll reveal IntersectionObserver -- */
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => {
        if (en.isIntersecting) {
          en.target.classList.add('in');
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });
    document.querySelectorAll('.reveal, .reveal-stagger').forEach(el => io.observe(el));

    /* -- Magnetic buttons -- */
    document.querySelectorAll('.blc-magnet').forEach(el => {
      const strength = parseFloat(el.dataset.magnet || '0.3');
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width  / 2) * strength;
        const y = (e.clientY - r.top  - r.height / 2) * strength;
        el.style.transform = `translate(${x}px,${y}px)`;
      });
      el.addEventListener('mouseleave', () => { el.style.transform = ''; });
    });

    /* -- Product card cursor light -- */
    document.querySelectorAll('.blc-prod-card').forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const r = card.getBoundingClientRect();
        card.style.setProperty('--mx', ((e.clientX - r.left) / r.width  * 100) + '%');
        card.style.setProperty('--my', ((e.clientY - r.top)  / r.height * 100) + '%');
      });
    });

    /* -- Mobile nav toggle -- */
    const menuToggle = document.querySelector('.blc-mobile-menu-toggle');
    const menuDrawer = document.querySelector('.blc-mobile-nav');
    menuToggle?.addEventListener('click', () => {
      const isOpen = menuDrawer?.classList.toggle('is-open');
      menuToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      document.body.style.overflow = isOpen ? 'hidden' : '';
    });

    /* -- Header scroll behavior (add shadow on scroll) -- */
    const header = document.querySelector('.blc-top');
    if (header) {
      const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  });

})();
